/**
 * API Error Types
 *
 * Standardized error codes and types for API responses.
 * All error codes follow E_* naming convention.
 */

/** Known API error codes */
export type ApiErrorCode =
	| 'E_USER_EMAIL_EXIST'
	| 'E_INVALID_CREDENTIALS'
	| 'E_USER_NOT_FOUND'
	| 'E_VALIDATION'
	| 'E_UNAUTHORIZED'
	| 'E_UNKNOWN'

/** Action link for error recovery */
export interface ApiErrorAction {
	label: string
	href: string
}

/** API error response shape */
export interface ApiErrorResponse {
	code: ApiErrorCode
	message: string
	action?: ApiErrorAction
}

/** Custom error class for API errors */
export class ApiError extends Error {
	code: ApiErrorCode
	action?: ApiErrorAction

	constructor(data: ApiErrorResponse) {
		super(data.message)
		this.name = 'ApiError'
		this.code = data.code
		this.action = data.action
	}

	/** Create from unknown response data with fallback */
	static fromResponse(data: unknown, fallbackMessage: string): ApiError {
		if (data && typeof data === 'object' && 'code' in data && 'message' in data) {
			const errorData = data as { code: string; message: string; action?: ApiErrorAction }
			return new ApiError({
				code: (errorData.code as ApiErrorCode) || 'E_UNKNOWN',
				message: errorData.message || fallbackMessage,
				action: errorData.action,
			})
		}

		// Legacy format: { error: "..." }
		if (data && typeof data === 'object' && 'error' in data) {
			const legacyData = data as { error: string }
			return new ApiError({
				code: 'E_UNKNOWN',
				message: legacyData.error || fallbackMessage,
			})
		}

		return new ApiError({
			code: 'E_UNKNOWN',
			message: fallbackMessage,
		})
	}
}
